import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { worldMapPaths } from './WorldMapData';
import { PRESSURE_VALVE_COLORS, getPressureValveLabel } from '../config/labelConfig';
import './WorldMap.css';

const WorldMap = ({
    countries = [],
    activeCountryCode,
    onCountrySelect,
    visualizationMode = 'default' // 'default' | 'pressure_valve'
}) => {
    const [hoveredCountry, setHoveredCountry] = useState(null);
    const [internalSelectedCountry, setInternalSelectedCountry] = useState(null);

    // Derived selected country (Controlled or Uncontrolled)
    const selectedCountry = activeCountryCode
        ? countries.find(c => c.code.toLowerCase() === activeCountryCode.toLowerCase())
        : internalSelectedCountry;

    const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);

    // Helper to check data availability
    const getDataCountry = (code) => {
        if (!code) return undefined;
        const lowerCode = code.toLowerCase();
        return countries.find(c => c?.code?.toLowerCase() === lowerCode);
    };

    // ... (Interaction Handlers remain mostly same, just need to update click handler)

    // ... [UseEffect for Wheel omitted for brevity in replace, assuring it stays] ...

    // Use ref for non-passive listener to properly prevent scroll
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const onWheel = (e) => {
            e.preventDefault();
            const scaleAmount = -e.deltaY * 0.002;

            setTransform(prev => {
                const newScale = Math.max(1, Math.min(8, prev.k * (1 + scaleAmount)));
                const rect = container.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;
                const newX = mouseX - (mouseX - prev.x) * (newScale / prev.k);
                const newY = mouseY - (mouseY - prev.y) * (newScale / prev.k);
                return { x: newX, y: newY, k: newScale };
            });
        };

        container.addEventListener('wheel', onWheel, { passive: false });
        return () => container.removeEventListener('wheel', onWheel);
    }, []);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            e.preventDefault();
            setTransform({
                ...transform,
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleCountryClick = (e, country) => {
        if (isDragging) return;
        e.preventDefault();
        e.stopPropagation();

        // 1. Notify Parent (Controller)
        if (onCountrySelect) {
            onCountrySelect(country);
        }

        // 2. Local fallback if no prop
        if (!activeCountryCode) {
            setInternalSelectedCountry(country);
        }

        // 3. Scroll logic
        scrollToCountry(country.code);
    };

    const scrollToCountry = (code) => {
        // Wait for React state update, DOM render, and card expansion animation (400ms)
        // Then scroll to position the card at the top of the viewport
        setTimeout(() => {
            const targetEl = document.getElementById(`country-${code}`);
            if (targetEl) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 500); // Increased delay to allow for card expansion animation
    };

    // --- Dynamic Display Logic ---
    // If hovering, show hovered. If not, show selected.
    // Display state for the indicator label
    const displayCountry = hoveredCountry || selectedCountry;

    // Improved isPreview check using codes to avoid reference issues
    const isPreview = !!hoveredCountry &&
        !!selectedCountry &&
        hoveredCountry.code.toLowerCase() !== selectedCountry.code.toLowerCase();

    // Label logic: If no country is selected, hovering is always a "Preview"
    // If a country is selected, hovering a DIFFERENT one is a "Preview"
    const labelText = (!selectedCountry || isPreview) ? "Preview" : "Selected";

    return (
        <div className="world-map-container" style={{ position: 'relative', overflow: 'hidden' }}>
            <div className="world-map-header">
                <h3>Global Reality Check</h3>
                <p>Select a country to see how their system actually works.</p>
            </div>

            <div
                ref={containerRef}
                className="svg-aspect-wrapper"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => {
                    handleMouseUp();
                    setHoveredCountry(null);
                }}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                <div className="world-map-svg-wrapper">
                    <svg
                        viewBox="0 140 1009 485"
                        preserveAspectRatio="xMidYMid slice"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={() => {
                            if (!isDragging && !activeCountryCode) setInternalSelectedCountry(null);
                        }}
                    >
                        <g transform={`translate(${transform.x}, ${transform.y}) scale(${transform.k})`}>
                            {worldMapPaths.map((path) => {
                                const dataCountry = getDataCountry(path.id);
                                const hasData = !!dataCountry;
                                const isSelected = selectedCountry?.code?.toLowerCase() === dataCountry?.code?.toLowerCase();

                                // Determine Fill Color
                                let fillColor = undefined;
                                if (visualizationMode === 'pressure_valve' && hasData) {
                                    const valve = dataCountry.classifiers?.pressure_valve_primary;
                                    if (valve && PRESSURE_VALVE_COLORS[valve]) {
                                        fillColor = PRESSURE_VALVE_COLORS[valve];
                                    } else {
                                        fillColor = '#cbd5e1'; // Slate-300
                                    }
                                }

                                return (
                                    <path
                                        key={path.id}
                                        d={path.d}
                                        id={path.id}
                                        title={path.name}
                                        className={`${hasData ? 'active-country-path' : ''} ${isSelected ? 'is-selected' : ''}`}
                                        style={{
                                            zIndex: isSelected ? 20 : 1,
                                            vectorEffect: 'non-scaling-stroke',
                                            fill: isSelected ? undefined : fillColor
                                        }}
                                        onClick={(e) => {
                                            if (!hasData) return;
                                            handleCountryClick(e, dataCountry);
                                        }}
                                        onMouseEnter={() => {
                                            if (hasData) setHoveredCountry(dataCountry);
                                        }}
                                        onMouseLeave={() => {
                                            if (hasData) {
                                                setHoveredCountry(prev => (prev?.code?.toLowerCase() === dataCountry.code.toLowerCase() ? null : prev));
                                            }
                                        }}
                                    />
                                );
                            })}
                        </g>
                    </svg>
                </div>
            </div>

            {/* Legend for Pressure Valve Mode */}
            <AnimatePresence>
                {visualizationMode === 'pressure_valve' && (
                    <motion.div
                        className="map-legend"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                    >
                        <div style={{ gridColumn: '1 / -1', marginBottom: '0.25rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                            Pressure Valves
                        </div>
                        {Object.keys(PRESSURE_VALVE_COLORS).map((key) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    background: PRESSURE_VALVE_COLORS[key],
                                    boxShadow: `0 0 6px ${PRESSURE_VALVE_COLORS[key]}40`
                                }} />
                                <span>{getPressureValveLabel(key)}</span>
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Active Country Indicator (Dynamic) */}
            <AnimatePresence>
                {displayCountry && (
                    <motion.div
                        key={displayCountry.code} // Key change triggers animation
                        className="active-country-indicator"
                        initial={{ opacity: 0, x: -15, scale: 0.95 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: -15, scale: 0.95 }}
                        transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 30,
                            opacity: { duration: 0.15 }
                        }}
                    >
                        <div className="active-country-content">
                            {displayCountry.code && (
                                <img
                                    src={`/image/flags/${displayCountry.code.toLowerCase()}.svg`}
                                    alt="flag"
                                    className="indicator-flag"
                                />
                            )}
                            <div className="indicator-text">
                                <span className="active-label">{labelText}</span>
                                <span className="active-name">{displayCountry.name}</span>
                                {visualizationMode === 'pressure_valve' && displayCountry.classifiers?.pressure_valve_primary && (
                                    <span style={{
                                        display: 'block',
                                        fontSize: '0.7rem',
                                        color: PRESSURE_VALVE_COLORS[displayCountry.classifiers.pressure_valve_primary],
                                        marginTop: '0.25rem',
                                        fontWeight: 500
                                    }}>
                                        {getPressureValveLabel(displayCountry.classifiers.pressure_valve_primary)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Tooltip (Hover only) - Optional now that we have the bottom indicator, but keeping for cursor feel if desired. 
                Actually, the user asked for label switching on the bottom. 
                Let's REMOVE the floating tooltip to avoid redundancy and clutter, 
                since the bottom left indicator now serves this purpose dynamically. 
            */}
            {/* REMOVED Floating Tooltip to rely on dynamic bottom-left indicator */}
        </div>
    );
};

export default WorldMap;
